import React from "react";
import _ from "lodash/fp";
import {
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Pencil as PencilIcon } from "@actinc/dls/icons/";

const TAG_TITLE = Object.freeze({
  DynamicBreakParagraphTag: "Break Paragraph",
  DynamicModifyContentTag: "Modify Content",
  DynamicRemoveLabelsTag: "Remove Labels",
});

const ItemChips = ({ items, tagItemIds, itemID }) => (
  <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
    {items
      .filter((item) => tagItemIds?.includes(item.id))
      .map((item) => (
        <Chip
          color={item.id === itemID ? "primary" : "default"}
          key={item.id}
          label={item.name}
          size="small"
        />
      ))}
  </Box>
);

const ModifyContentSecondary = ({ tag, items, itemID }) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    <Box dangerouslySetInnerHTML={{ __html: tag.replaceWith }}></Box>
    <Box sx={{ display: "flex", flexDirection: "row" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          paddingRight: 2,
        }}
      >
        <Chip
          color={tag.highlight ? "primary" : "default"}
          key="highlight"
          label="H"
          size="small"
        />
        <Chip
          color={tag.underline ? "primary" : "default"}
          key="underline"
          label="U"
          size="small"
        />
      </Box>
      <ItemChips items={items} tagItemIds={tag.items} itemID={itemID} />
    </Box>
  </Box>
);

const RemoveLabelsSecondary = ({
  tag,
  items,
  itemID,
  dynamicContentLabels,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {["paragraph", "sentence"].map((type) => (
      <Box
        key={type}
        sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
      >
        <Typography sx={{ marginRight: 1 }} variant="subtitle2">
          {_.capitalize(type)}:
        </Typography>
        {dynamicContentLabels
          ?.filter((o) => o.type === type)
          .map((label) => (
            <Chip
              color={
                tag.labels.some(
                  (o) => o.begin === label.begin && o.end === label.end,
                )
                  ? "primary"
                  : "default"
              }
              key={label.begin}
              label={label.content}
              size="small"
            />
          ))}
      </Box>
    ))}
    <ItemChips items={items} tagItemIds={tag.items} itemID={itemID} />
  </Box>
);

// Renders one row of the Dynamic Tags list. Centralizes the shared shell
// (edit pencil on the left, title + secondary content in the middle, delete
// trash on the right) so the four tag types only differ in the secondary
// content and (for OrderContent) the absence of mouseover hover handlers.
const DynamicTagListItem = ({
  tag,
  index,
  passageItems,
  itemID,
  readonly,
  dynamicContentLabels,
  orderContentType, // pre-computed by parent (Sentences/Paragraphs)
  onEdit,
  onDelete,
  onMouseEnter,
  onMouseLeave,
}) => {
  const editAriaLabel =
    tag.__typename === "DynamicModifyContentTag" ||
    tag.__typename === "DynamicRemoveLabelsTag"
      ? "edit"
      : undefined;

  let title;
  let secondary;
  switch (tag.__typename) {
    case "DynamicBreakParagraphTag":
      title = TAG_TITLE.DynamicBreakParagraphTag;
      secondary = (
        <ItemChips
          items={passageItems}
          tagItemIds={tag.items}
          itemID={itemID}
        />
      );
      break;
    case "DynamicModifyContentTag":
      title = TAG_TITLE.DynamicModifyContentTag;
      secondary = (
        <ModifyContentSecondary
          tag={tag}
          items={passageItems}
          itemID={itemID}
        />
      );
      break;
    case "DynamicOrderContentTag":
      title = `Reorder ${orderContentType}`;
      secondary = (
        <ItemChips
          items={passageItems}
          tagItemIds={tag.items}
          itemID={itemID}
        />
      );
      break;
    case "DynamicRemoveLabelsTag":
      title = TAG_TITLE.DynamicRemoveLabelsTag;
      secondary = (
        <RemoveLabelsSecondary
          tag={tag}
          items={passageItems}
          itemID={itemID}
          dynamicContentLabels={dynamicContentLabels}
        />
      );
      break;
    default:
      return null;
  }

  return (
    <ListItem
      key={tag.id}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{ borderTop: "1px solid lightgrey" }}
    >
      <ListItemIcon>
        <IconButton aria-label={editAriaLabel} onClick={onEdit} size="small">
          <PencilIcon />
        </IconButton>
      </ListItemIcon>
      <ListItemText disableTypography={true} secondary={secondary}>
        <Typography variant="subtitle1">{title}</Typography>
      </ListItemText>
      <ListItemSecondaryAction
        sx={{ display: "flex", flexDirection: "column" }}
      >
        {!readonly && (
          <IconButton
            aria-label="delete"
            onClick={() => onDelete(index)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export { DynamicTagListItem };
